import type { PrintJobUpdatedEvent, PrintingStatus, TicketKind } from '../types/printing.types';
import { ticketPhrase } from './printing-format';

/* ==========================================================================
   Aviso de cupom sem confirmação

   Dispara no `printing.job_updated` com `overdue = true`: uma vez por
   trabalho em cada aba, ou uma vez por pedido quando o evento não traz
   trabalho (o despacho isolado, `dispatch_failed`). O registro do que já
   tocou vive no módulo, e não no componente, porque é por aba: navegar entre
   páginas não pode fazer o mesmo cupom tocar de novo.

   A faixa junta os cupons de um pedido numa linha só ("Pedido #3: a
   conferência e a comanda da Cozinha podem não ter saído. Confira a
   impressora."), e o som toca uma vez por pedido. Com o computador offline
   ou a impressão pausada, suspensa ou desligada, a faixa vermelha ou o aviso
   de pausa já dizem a causa, e as linhas por pedido ficam de fora (relatório
   de UX, P1-11); o cupom que sai depois tira a linha dele.
   ========================================================================== */

const alreadyAlerted = new Set<string>();

/**
 * A chave do aviso, ou nulo quando o evento não pede aviso: sem atraso, sem
 * pedido (a página de teste), ou um cupom que já saiu da fila — o cancelado
 * pela pausa ou pela desativação chega com `overdue = false` e não toca.
 */
export function overdueAlertKey(event: PrintJobUpdatedEvent): string | null {
  if (!event.overdue || !event.orderId) return null;
  if (event.jobId) {
    if (event.status !== 'pending' && event.status !== 'delivered') return null;
    return `job:${event.jobId}`;
  }
  if (event.errorCode !== 'dispatch_failed') return null;
  return `order:${event.orderId}`;
}

/** Marca o aviso como dado nesta aba; falso quando ele já tinha tocado. */
export function claimOverdueAlert(key: string): boolean {
  if (alreadyAlerted.has(key)) return false;
  alreadyAlerted.add(key);
  return true;
}

/** Só para teste: esquece os avisos dados nesta aba. */
export function resetOverdueAlerts(): void {
  alreadyAlerted.clear();
}

/** Um cupom atrasado da linha do pedido. */
export interface OverdueTicket {
  key: string;
  kind: TicketKind | null;
  stationName: string | null;
}

/** A linha de um pedido na faixa: os cupons que podem não ter saído, ou os cupons não gerados. */
export interface OverdueOrder {
  orderId: string;
  dailyNumber: number | null;
  tickets: readonly OverdueTicket[];
  /** O despacho isolado (`dispatch_failed`): os cupons nem foram gerados. */
  notGenerated: boolean;
}

/** Quantos pedidos a faixa mostra; o mais novo entra no topo. */
export const MAX_OVERDUE_ORDERS = 3;

/**
 * Junta o evento já aceito por `claimOverdueAlert` à linha do pedido dele.
 * Devolve a lista nova e se o pedido é novo na faixa (o som toca uma vez por
 * pedido, e não por cupom).
 */
export function addOverdue(
  orders: readonly OverdueOrder[],
  event: PrintJobUpdatedEvent,
  key: string,
): { orders: OverdueOrder[]; newOrder: boolean } {
  const orderId = event.orderId!;
  const current = orders.find((order) => order.orderId === orderId);
  const base: OverdueOrder = current ?? { orderId, dailyNumber: event.dailyNumber, tickets: [], notGenerated: false };
  const next: OverdueOrder = event.jobId
    ? { ...base, tickets: [...base.tickets, { key, kind: event.kind, stationName: event.stationName }] }
    : { ...base, notGenerated: true };
  const rest = orders.filter((order) => order.orderId !== orderId);
  return { orders: [next, ...rest].slice(0, MAX_OVERDUE_ORDERS), newOrder: !current };
}

/**
 * Tira da faixa o cupom que saiu (ou foi cancelado com o pedido) depois do
 * atraso; a linha some quando não sobra cupom.
 */
export function resolveOverdue(orders: readonly OverdueOrder[], event: PrintJobUpdatedEvent): readonly OverdueOrder[] {
  if (!event.jobId || (event.status !== 'printed' && event.status !== 'cancelled')) return orders;
  const key = `job:${event.jobId}`;
  if (!orders.some((order) => order.tickets.some((ticket) => ticket.key === key))) return orders;
  return orders
    .map((order) => ({ ...order, tickets: order.tickets.filter((ticket) => ticket.key !== key) }))
    .filter((order) => order.tickets.length > 0 || order.notGenerated);
}

/** "a conferência", "a conferência e a comanda da Cozinha", "a conferência, a comanda da Cozinha e a via de entrega". */
function joinPhrases(phrases: readonly string[]): string {
  if (phrases.length <= 1) return phrases[0] ?? '';
  return `${phrases.slice(0, -1).join(', ')} e ${phrases.at(-1)}`;
}

/** "Pedido #3", ou só "Pedido" no pedido sem número do dia. */
export function overdueOrderLabel(dailyNumber: number | null): string {
  return dailyNumber != null ? `Pedido #${dailyNumber}` : 'Pedido';
}

/**
 * "Pedido #3: a conferência e a comanda da Cozinha podem não ter saído.
 * Confira a impressora.", ou "Pedido #3: os cupons não foram gerados." no
 * despacho isolado. Número e ponto vêm do próprio evento, sem leitura extra.
 */
export function describeOverdueOrder(order: OverdueOrder): string {
  const label = overdueOrderLabel(order.dailyNumber);
  const phrases = [...new Set(order.tickets.map((ticket) => ticketPhrase(ticket.kind, ticket.stationName)))];
  if (phrases.length === 0) return `${label}: os cupons não foram gerados.`;
  const verb = phrases.length === 1 ? 'pode não ter saído' : 'podem não ter saído';
  return `${label}: ${joinPhrases(phrases)} ${verb}. Confira a impressora.`;
}

/**
 * Com o computador offline (inclusive o recusado por protocolo), a impressão
 * pausada, suspensa pela assinatura ou desligada pela plataforma, a faixa
 * vermelha ou o aviso de pausa já dizem por que o papel não sai: as linhas
 * por pedido repetiriam o mesmo problema.
 */
export function overdueLinesSuppressed(status: PrintingStatus | undefined): boolean {
  if (!status?.active) return false;
  return (
    status.paused ||
    status.accessBlocked ||
    status.platformDisabled ||
    status.agent?.updateRequired === true ||
    status.problems.some((problem) => problem.code === 'agent_offline')
  );
}
